import { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ProjectKnowledge from "../components/ProjectKnowledge";
import DocumentViewer from "../components/DocumentViewer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { MessageSquare, FileText, Plus, Trash2, Edit3, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import { useTaal } from "@/contexts/TaalContext";
import { leesActiefTeam, verplaatsDoelen, PERSOONLIJK } from "@/lib/actiefTeam";
// NS-A2: lui geladen, want het bekijkvenster trekt react-markdown mee; alleen nodig als er een document staat.
const ArtefactDocument = lazy(() => import("../components/ArtefactDocument"));


/** The one thing a project quietly does for you, said the same way in both layouts. */
const CONTEXT_NOTE =
  "Every chat you start in this project is answered with its documents searched first. " +
  "Add what matters here once, and you never have to attach it again.";

export default function ProjectPage() {
  const { t } = useTaal();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editColor, setEditColor] = useState("#6366f1");
  const [viewingDocument, setViewingDocument] = useState(null);
  const [artifactDialog, setArtifactDialog] = useState(false);
  const [newArtifactName, setNewArtifactName] = useState("");
  const [newArtifactContent, setNewArtifactContent] = useState("");

  const fetchProject = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/projects/${projectId}`);
      setProject(resp.data);
      setEditName(resp.data.name);
      setEditDescription(resp.data.description);
      setEditColor(resp.data.color);
    } catch { toast.error(t("Project niet gevonden")); navigate("/"); }
    finally { setLoading(false); }
  }, [projectId, navigate, t]);

  const fetchConversations = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/conversations?project_id=${projectId}`);
      setConversations(resp.data);
    } catch {}
  }, [projectId]);

  const fetchArtifacts = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/artifacts?project_id=${projectId}`);
      setArtifacts(resp.data);
    } catch {}
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchProject();
      fetchConversations();
      fetchArtifacts();
    }
  }, [projectId, fetchProject, fetchConversations, fetchArtifacts]);

  // K7d (19 okt): de teams en het actieve team, voor "Naar werkruimte".
  const [teams, setTeams] = useState([]);
  const [actiefTeam, setActiefTeam] = useState(PERSOONLIJK);
  useEffect(() => {
    axios.get(`${API}/teams`).then((r) => setTeams(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    leesActiefTeam().then(setActiefTeam).catch(() => {});
  }, []);
  const verplaats = async (keuze) => {
    try {
      await axios.put(`${API}/projects/${projectId}/team`, { team_id: keuze === "persoonlijk" ? null : keuze });
      toast.success(t("Project verplaatst"));
      navigate("/");
    } catch { toast.error(t("Project verplaatsen mislukt")); }
  };

  const handleUpdateProject = async () => {
    try {
      const resp = await axios.put(`${API}/projects/${projectId}`, { name: editName, description: editDescription, color: editColor });
      setProject(resp.data);
      setEditing(false);
      toast.success(t("Project bijgewerkt"));
    } catch { toast.error(t("Project bijwerken mislukt")); }
  };

  // GLASRONDE 0.9.190 (Frank 23 okt): eerst een vraag in de taal van de app, en pas
  // na "Verwijderen" de aanroep. Stond hier als vaste Engelse `window.confirm`.
  const [verwijderVraag, setVerwijderVraag] = useState(false);
  const handleDeleteProject = async () => {
    setVerwijderVraag(false);
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success(t("Project verwijderd"));
      navigate("/");
    } catch { toast.error(t("Project verwijderen mislukt")); }
  };

  const handleNewChat = async () => {
    // Same call the sidebar makes, with the project already filled in. This is
    // the whole point of standing on a project screen: you do not pick the
    // project afterwards.
    try {
      const resp = await axios.post(`${API}/conversations?project_id=${projectId}`);
      navigate(`/chat/${resp.data.id}`);
    } catch { toast.error(t("Een chat beginnen mislukt")); }
  };

  const handleCreateArtifact = async () => {
    if (!newArtifactName.trim() || !newArtifactContent.trim()) return;
    try {
      await axios.post(`${API}/artifacts`, { name: newArtifactName, content: newArtifactContent, conversation_id: conversations[0]?.id || null });
      setArtifactDialog(false);
      setNewArtifactName("");
      setNewArtifactContent("");
      fetchArtifacts();
      toast.success(t("Artefact aangemaakt"));
    } catch { toast.error(t("Artefact aanmaken mislukt")); }
  };

  const handleDeleteArtifact = async (id) => {
    if (!window.confirm("Delete this artifact?")) return;
    try {
      await axios.delete(`${API}/artifacts/${id}`);
      fetchArtifacts();
      toast.success(t("Artefact verwijderd"));
    } catch { toast.error(t("Artefact verwijderen mislukt")); }
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><p className="text-muted-foreground">{t("Laden...")}</p></div>;
  if (!project) return null;

  return (
    <div className={`flex flex-col h-screen ${BOTTOM_NAV_SPACER}`}>
      <header className="border-b px-4 sm:px-6 py-3 flex items-center gap-4 pt-[calc(0.75rem+var(--veilig-boven))]">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          {editing ? (
            <div className="flex items-center gap-2">
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="max-w-[300px]" />
              <input type="color" value={editColor} onChange={(e) => setEditColor(e.target.value)} className="w-8 h-8 rounded cursor-pointer" />
              <Button size="sm" onClick={handleUpdateProject}>{t("Opslaan")}</Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>{t("Annuleren")}</Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: project.color }} />
              <h1 className="text-lg font-semibold">{project.name}</h1>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setEditing(true)}>
                <Edit3 className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => setVerwijderVraag(true)}
                data-testid="project-verwijderen" aria-label={t("Project verwijderen")}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              {/* K7d (19 okt): het project met zijn banken en gesprekken naar een andere
                  werkruimte. Alleen als er teams zijn; daarna terug naar het startscherm,
                  want het project hoort dan niet meer bij deze werkruimte. */}
              {teams.length > 0 && (
                <select value="" onChange={(e) => verplaats(e.target.value)} data-testid="project-naar-werkruimte"
                        aria-label={t("Naar werkruimte")}
                        className="h-11 md:h-7 rounded-md border border-input bg-background px-2 text-xs">
                  <option value="" disabled>{t("Naar werkruimte")}</option>
                  {verplaatsDoelen(teams, actiefTeam, t).map((o) => (
                    <option key={o.id || "persoonlijk"} value={o.id || "persoonlijk"}>{o.naam}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          {project.description && !editing && (
            <p className="text-sm text-muted-foreground mt-1">{project.description}</p>
          )}
        </div>
      </header>

      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* On a phone this column IS the page. It used to be capped at
            max-h-48: three sections crammed into 192 points with the empty
            middle panel taking the rest of the screen, so the chats of the
            project were a scrap of a scroll box. */}
        <div className="w-full flex-1 md:flex-none md:w-80 border-b md:border-b-0 md:border-r p-4 overflow-y-auto space-y-6 md:shrink-0">
          <Button className="w-full justify-center gap-2 h-11 md:h-9" data-testid="project-new-chat" onClick={handleNewChat}>
            <Plus className="h-4 w-4" />
            <span>{t("Nieuwe chat in dit project")}</span>
          </Button>

          <p className="text-sm text-muted-foreground md:hidden" data-testid="project-context-note-mobile">
            {CONTEXT_NOTE}
          </p>

          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-medium text-muted-foreground uppercase">{t("Chats in dit project")}</h2>
              <span className="text-xs text-muted-foreground" data-testid="project-chat-count">{conversations.length}</span>
            </div>
            {conversations.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="project-chats-empty">
                {t("Nog geen chats. Begin er een met de knop hierboven en hij komt hier terecht.")}
              </p>
            ) : (
              <div className="space-y-1" data-testid="project-chat-list">
                {conversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => navigate(`/chat/${conv.id}`)}
                    className="tap-target w-full flex items-center gap-2 px-2 py-2.5 text-sm rounded hover:bg-muted transition-colors text-left"
                  >
                    <MessageSquare className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                    {/* min-w-0, same reason as the document rows: without it a
                        long title refuses to shrink and pushes the row wide. */}
                    <span className="truncate min-w-0">{conv.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <ProjectKnowledge projectId={projectId} onOpenDocument={setViewingDocument} />

          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-medium text-muted-foreground uppercase">{t("Artefacten")}</h2>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setArtifactDialog(true)}>
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
            {artifacts.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("Nog geen artefacten")}</p>
            ) : (
              <div className="space-y-1">
                {artifacts.map((art) => (
                  <div key={art.id} className="group flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted transition-colors">
                    {art.bijlage ? (
                      <Suspense fallback={null}><ArtefactDocument artefact={art} className="flex-1" /></Suspense>
                    ) : (
                    <button
                      onClick={() => navigate(`/project/${projectId}/artifact/${art.id}`)}
                      className="flex items-center gap-2 flex-1 min-w-0 text-left"
                    >
                      <FileText className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                      <span className="truncate">{art.name}</span>
                    </button>
                    )}
                    <button
                      onClick={() => handleDeleteArtifact(art.id)}
                      className="opacity-100 md:opacity-0 md:group-hover:opacity-100 text-destructive hover:text-destructive/80"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Desktop only. On a phone this pane pushed the actual contents of
            the project into a sliver, and its text lives at the top of the
            column there instead. */}
        <div className="hidden md:flex flex-1 items-center justify-center p-6">
          <div className="text-center space-y-3" data-testid="project-context-note">
            <Sparkles className="h-12 w-12 mx-auto text-muted-foreground" />
            <h3 className="text-lg font-medium">{project.name}</h3>
            <p className="text-sm text-muted-foreground max-w-md">{CONTEXT_NOTE}</p>
          </div>
        </div>
      </div>

      <Dialog open={verwijderVraag} onOpenChange={setVerwijderVraag}>
        <DialogContent data-testid="project-verwijder-vraag">
          <DialogHeader><DialogTitle>{t("Project {naam} verwijderen?", { naam: project?.name || "" })}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t("Alleen het project verdwijnt. De chats gaan naar Geen project; kennisbanken, documenten en geheugen blijven bestaan.")}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerwijderVraag(false)}>{t("Annuleren")}</Button>
            <Button variant="destructive" data-testid="project-verwijder-ja" onClick={handleDeleteProject}>{t("Verwijderen")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={artifactDialog} onOpenChange={setArtifactDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("Nieuw artefact")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t("Naam van het artefact")} value={newArtifactName} onChange={(e) => setNewArtifactName(e.target.value)} />
            <Textarea placeholder={t("Inhoud...")} value={newArtifactContent} onChange={(e) => setNewArtifactContent(e.target.value)} className="min-h-[200px]" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArtifactDialog(false)}>{t("Annuleren")}</Button>
            <Button onClick={handleCreateArtifact}>{t("Aanmaken")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {viewingDocument && (
        <DocumentViewer document={viewingDocument} onClose={() => setViewingDocument(null)} />
      )}
      <MobileBottomNav />
    </div>
  );
}
